import React from 'react';
import SetPageTitle from 'components/Portals/SetPageTitle';
import { Routes } from 'routing';
import { Trans, useTranslation } from 'services/localization';

import TabContainer from 'components/nav/TabContainer';
import AllBlocks from './AllBlocks';
import InstalledBlocks from './InstalledBlocks';
import { Box, Button, Typography, useMediaQuery, useTheme } from '@mui/material';
import * as ShopSelectors from 'selectors/ShopSelectors';
import { useSelector } from 'react-redux';
import { getUsedBlockSlots, getTotalBlockSlots, isLitePlan } from 'common/modules/plans';
import ExternalLink from 'components/ExternalLink';
import { Link, useLocation } from 'react-router-dom';
import Container from 'components/Container';
import HasFeature from 'components/features/HasFeature';

const BlockStore = () => {
	const { t } = useTranslation();
	const theme = useTheme();
	const shopFeatures = useSelector(ShopSelectors.shopFeatures);
	const plan = useSelector(ShopSelectors.shopPlan);
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const location = useLocation();

	const isLite = isLitePlan(plan);
	const usedBlockSlots = getUsedBlockSlots(shopFeatures);

	const totalBlockSlots = getTotalBlockSlots(shopFeatures);

	return (
		<>
			<SetPageTitle
				priority={2}
				isMobile={isMobile}
				title={t('addOns.pageTitle', 'Blocks')}
				caption={
					<Box
						sx={{
							fontWeight: 400,
							width: '55%',
							marginBottom: theme.spacing(1),
							[theme.breakpoints.down('sm')]: {
								width: '100%',
							},
						}}
					>
						<Trans
							i18nKey="account.addOns.pageSubHeader"
							defaults="Discover new features for your store with building blocks. Can’t find the block you are looking for? <supportLink>Let us know</supportLink>"
							components={{
								supportLink: (
									<ExternalLink href="https://feedback.rentle.io">{'Let us know'}</ExternalLink>
								),
							}}
						/>
					</Box>
				}
				rightContent={
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'row',
							alignItems: 'center',
							justifyContent: 'center',
							[theme.breakpoints.down('sm')]: {
								marginTop: theme.spacing(1),
							},
						}}
					>
						<Box
							sx={{
								border: `1px solid ${theme.palette.border.inputOutline}`,
								borderRadius: theme.spacing(0.5, 0, 0, 0.5),
								background: theme.palette.background.default,
							}}
						>
							<Typography
								sx={{
									fontWeight: 400,
									margin: theme.spacing(1.5, 2),
								}}
								variant="body2"
							>
								{isLite
									? t('account.addOns.upgradeToStartUsingBlocks', 'Upgrade to start using blocks')
									: t('account.addOns.blocksUsed', {
											usedBlocks: usedBlockSlots,
											totalBlocks: totalBlockSlots,
											defaultValue: '{{usedBlocks}}/{{totalBlocks}} blocks used',
									  })}
							</Typography>
						</Box>
						<HasFeature requiredFeature="PLAN_BILLING">
							<Box
								sx={{
									border: `1px solid ${theme.palette.border.inputOutline}`,
									borderLeft: 'none',
									borderRadius: theme.spacing(0, 0.5, 0.5, 0),
									background: theme.palette.background.default,
								}}
							>
								<Link to={{ pathname: Routes.PLANS, state: { from: location.pathname } }}>
									<Button
										sx={{
											padding: theme.spacing(1.5, 2),
											borderRadius: 0,
											lineHeight: 1.43,
										}}
										color="success"
										variant="text"
									>
										{t('shop.billingView.changePlan')}
									</Button>
								</Link>
							</Box>
						</HasFeature>
					</Box>
				}
			/>
			<Container>
				<TabContainer
					basePath={Routes.BLOCKS_STORE}
					tabs={[
						{
							label: t('account.addOns.tabs.all', 'All'),
							component: AllBlocks,
							path: 'all',
						},
						{
							label: t('account.addOns.tabs.installed', 'Installed'),
							component: InstalledBlocks,
							path: 'installed',
						},
					]}
				/>
			</Container>
		</>
	);
};

export default BlockStore;
